import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { eachDayOfInterval, format, isSameDay, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso } from 'react-virtuoso';
import { Box, Button, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { MapDownIcon } from '~/assets/icons';
import JourneyTimeline from './JourneyTimeline';
import { JourneyData, JourneyPoint, TimelineItemUI } from '~/utils/interface/mapback';
import {
  durationStrToSeconds,
  formatDuration,
  formatDistance,
  formatTimeHM,
  formatBattery,
  secondsToDurationStr,
} from '../utils/formatters';
import { getJourneySegmentTypeActiveBg, getJourneySegmentTypeColor } from '../constants/segmentTypeColors';
import { useJourneyPersonStatuses } from '../context/JourneyPersonStatusContext';

const DetailTableScroller = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
  const { style, className, ...rest } = props;
  return (
    <Box
      component='div'
      {...rest}
      ref={ref}
      className={`h-full overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-[2px] [&::-webkit-scrollbar-thumb]:bg-[#E3E7EC] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1 ${className ?? ''}`}
      style={{ ...style, scrollbarWidth: 'thin', scrollbarColor: '#E3E7EC transparent' }}
    />
  );
});

DetailTableScroller.displayName = 'DetailTableScroller';

const DetailTableColGroup: React.FC = () => (
  <colgroup>
    <col style={{ width: '20%' }} />
    <col style={{ width: '24%' }} />
    <col style={{ width: '38%' }} />
    <col style={{ width: '18%' }} />
  </colgroup>
);

const DetailTableElement: React.FC<React.TableHTMLAttributes<HTMLTableElement>> = (props) => {
  const { className, style, children, ...rest } = props;
  return (
    <table
      {...rest}
      className={`w-full border-separate border-spacing-0 text-[16px] ${className ?? ''}`}
      style={{ ...style, tableLayout: 'fixed', fontSize: '16px' }}
    >
      <DetailTableColGroup />
      {children}
    </table>
  );
};

const DetailTableRowElement: React.FC<React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
  const { className, ...rest } = props;
  return <tr {...rest} className={`h-[56px] ${className ?? ''}`} />;
};

type DetailTableRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
  item: DetailRow;
  context?: {
    onRowClick?: (row: DetailRow) => void;
    onRowHover?: (rowId: string | null) => void;
  };
};

const ClickableDetailTableRow: React.FC<DetailTableRowProps> = ({ className, item, context, onClick, ...rest }) => (
  <tr
    {...rest}
    className={`h-[56px] cursor-pointer transition-colors ${className ?? ''}`}
    onMouseEnter={() => context?.onRowHover?.(item.id)}
    onMouseLeave={() => context?.onRowHover?.(null)}
    onClick={(event) => {
      onClick?.(event);
      context?.onRowClick?.(item);
    }}
  />
);

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface JourneySidebarProps {
  journeyData: JourneyData | null;
  selectedPersonInfo?: Pick<JourneyData, 'person_id' | 'full_name' | 'device_imei'> | null;
  dateRange: DateRange;
  timelineItems?: TimelineItemUI[];
  allPoints?: JourneyPoint[];
  selectedDate?: Date | null;
  onSelectedDateChange?: (date: Date | null) => void;
  isLoading: boolean;
  hasValidDateRange?: boolean;
  currentPointIndex?: number;
  enableActiveHighlight?: boolean;
  onDetailRowClick?: (row: SidebarDetailRowClickPayload) => void;
  activeTab?: 'timeline' | 'detail';
  onTabChange?: (tab: 'timeline' | 'detail') => void;
  onTimelineSegmentClick?: (segmentIndex: number) => void;
  selectedTimelineSegmentIndex?: number | null;
  onTimelineSegmentDoubleClick?: (segmentIndex: number) => void;
}

export interface SidebarDetailRowClickPayload {
  id: string;
  segmentIndex: number;
  segmentIndices: number[];
  pointIndex: number | null;
  latitude: number | null;
  longitude: number | null;
}

interface StatRowProps {
  label: string;
  value: string;
}

interface DetailRow {
  id: string;
  segmentIndex: number;
  segmentIndices: number[];
  segmentType: JourneyPoint['segmentType'];
  pointId: number | null;
  pointIndex: number | null;
  latitude: number | null;
  longitude: number | null;
  dateKey: string;
  sortKey: string;
  time: string;
  speed: string;
  statusDuration: string;
  durationSeconds: number;
  address: string;
  battery: string;
}

function resolvePointCoordinates(point: JourneyPoint | null): { latitude: number; longitude: number } | null {
  if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return null;
  return { latitude: point.latitude, longitude: point.longitude };
}

function resolvePointIndexInAllPoints(
  allPoints: JourneyPoint[],
  segmentIndex: number,
  point: JourneyPoint | null,
  pointId: number | null
): number | null {
  if (!point) return null;

  if (pointId != null) {
    const byId = allPoints.findIndex((p) => p.segmentIndex === segmentIndex && p.id === pointId);
    if (byId >= 0) return byId;
  }

  const byTimestamp = allPoints.findIndex((p) => p.segmentIndex === segmentIndex && p.timestamp === point.timestamp);
  return byTimestamp >= 0 ? byTimestamp : null;
}

function toSidebarDetailRowClickPayload(row: DetailRow): SidebarDetailRowClickPayload {
  return {
    id: row.id,
    segmentIndex: row.segmentIndex,
    segmentIndices: row.segmentIndices,
    pointIndex: row.pointIndex,
    latitude: row.latitude,
    longitude: row.longitude,
  };
}

function StatRow({ label, value }: StatRowProps) {
  return (
    <Box className='grid min-h-[56px] grid-cols-2 items-center'>
      <Box className='px-3'>
        <Typography
          component='span'
          className='text-[16px] font-medium leading-[140%] text-[#404854]'
          style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
        >
          {label}
        </Typography>
      </Box>
      <Box className='flex h-full items-center border-l border-[#D7DEE7] px-3 text-left'>
        <Typography
          component='span'
          className='text-[16px] font-normal leading-[140%] text-[#161A1F]'
          style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function SummaryTitle({ dateRange }: { dateRange: DateRange }) {
  const { t } = useTranslation('admin/journey');
  if (!dateRange.start || !dateRange.end) {
    return (
      <Typography
        component='h3'
        className='m-0 text-[18px] font-semibold leading-[140%] text-[#161A1F]'
        style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
      >
        {t('sidebar.summarySingleDay')}
      </Typography>
    );
  }
  const isSingleDay = isSameDay(dateRange.start, dateRange.end);
  const rangeText = `${format(dateRange.start, 'dd/MM/yyyy')} - ${format(dateRange.end, 'dd/MM/yyyy')}`;

  if (isSingleDay) {
    return (
      <Typography
        component='h3'
        className='m-0 text-[18px] font-semibold leading-[140%] text-[#161A1F]'
        style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
      >
        {t('sidebar.summarySingleDay')}
      </Typography>
    );
  }

  return (
    <Typography
      component='h3'
      className='m-0 flex items-center gap-1.5 text-[18px] font-semibold leading-[140%] text-[#161A1F]'
      style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
    >
      <Typography component='span' variant='inherit' style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}>
        {t('sidebar.summaryRangePrefix')}
      </Typography>
      <Typography
        component='span'
        className='text-[16px] font-normal leading-[140%] text-[#404854]'
        style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
      >
        {rangeText}
      </Typography>
    </Typography>
  );
}

function SummaryStatsCard({ journeyData, dateRange, isLoading }: JourneySidebarProps) {
  const { t } = useTranslation('admin/journey');

  const stats = useMemo(() => {
    if (!journeyData) return null;
    const segments = Array.isArray(journeyData.segments) ? journeyData.segments : [];
    const formatHourMinute = (seconds: number): string => {
      const safeSeconds = Math.max(0, Math.floor(seconds));
      const hours = Math.floor(safeSeconds / 3600);
      const minutes = Math.floor((safeSeconds % 3600) / 60);

      if (hours > 0 && minutes > 0) {
        return `${t('format.durationDetailed.hour', { count: hours })} ${t('format.durationDetailed.minute', {
          count: minutes,
        })}`;
      }
      if (hours > 0) return t('format.durationDetailed.hour', { count: hours });
      return t('format.durationDetailed.minute', { count: minutes });
    };

    const routeSecs = segments
      .filter((s) => s.type === 'route')
      .reduce((acc, s) => acc + durationStrToSeconds(s.status_duration), 0);

    const stopSecs = segments
      .filter((s) => s.type === 'stop')
      .reduce((acc, s) => acc + durationStrToSeconds(s.status_duration), 0);

    const totalDistance = segments.reduce((acc, s) => acc + (s.total_distance ?? 0), 0);

    return {
      movingTime: formatHourMinute(routeSecs),
      stopTime: formatHourMinute(stopSecs),
      totalDistance: formatDistance(totalDistance),
    };
  }, [journeyData, t]);

  const fallbackStats = useMemo(
    () => ({
      movingTime: t('format.durationDetailed.minute', { count: 0 }),
      stopTime: t('format.durationDetailed.minute', { count: 0 }),
      totalDistance: formatDistance(0),
    }),
    [t]
  );

  const displayStats = stats ?? fallbackStats;

  return (
    <Box className='flex min-h-[237px] w-full flex-col gap-4 rounded-xl border border-gray-200 bg-white px-3 pb-4 pt-3 shadow-sm'>
      <SummaryTitle dateRange={dateRange} />

      {isLoading ? (
        <Box className='flex h-[168px] flex-col justify-center gap-3 rounded-xl border border-[#D7DEE7] px-3'>
          {[1, 2, 3].map((i) => (
            <Box key={i} className='flex justify-between'>
              <Box className='h-4 w-2/5 animate-pulse rounded bg-gray-200' />
              <Box className='h-4 w-1/5 animate-pulse rounded bg-gray-200' />
            </Box>
          ))}
        </Box>
      ) : (
        <Box className='h-[168px] overflow-hidden rounded-xl border border-[#D7DEE7]'>
          <StatRow label={t('sidebar.movingTime')} value={displayStats.movingTime} />
          <Box className='border-t border-[#D7DEE7]' />
          <StatRow label={t('sidebar.stoppedTime')} value={displayStats.stopTime} />
          <Box className='border-t border-[#D7DEE7]' />
          <StatRow label={t('sidebar.totalDistance')} value={displayStats.totalDistance} />
        </Box>
      )}
    </Box>
  );
}

function DetailTableCard({
  dateRange,
  journeyData,
  timelineItems,
  allPoints = [],
  selectedDate,
  onSelectedDateChange,
  isLoading,
  hasValidDateRange = false,
  currentPointIndex = 0,
  enableActiveHighlight = false,
  onDetailRowClick,
}: JourneySidebarProps) {
  const { t } = useTranslation('admin/journey');
  const personStatuses = useJourneyPersonStatuses();
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isAllDatesSelected, setIsAllDatesSelected] = useState(true);
  const [hoveredDropdownOptionKey, setHoveredDropdownOptionKey] = useState<string | null>(null);
  const [hoveredDetailRowId, setHoveredDetailRowId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const safePoints = useMemo(() => (Array.isArray(allPoints) ? allPoints : []), [allPoints]);

  // Build date options from the header date range so this dropdown always follows selected range.
  const rangeDates = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return [];
    const start = startOfDay(dateRange.start);
    const end = startOfDay(dateRange.end);
    if (start.getTime() > end.getTime()) return [];

    return eachDayOfInterval({ start, end }).map((d) => ({
      key: format(d, 'yyyy-MM-dd'),
      label: format(d, 'dd/MM/yyyy'),
      date: d,
    }));
  }, [dateRange.end, dateRange.start]);

  // Active date key
  const activeDateKey = useMemo(() => {
    if (selectedDate) return format(selectedDate, 'yyyy-MM-dd');
    return rangeDates[0]?.key ?? null;
  }, [rangeDates, selectedDate]);

  // Points filtered by active date, sorted ascending
  const detailRows = useMemo<DetailRow[]>(() => {
    if (!hasValidDateRange) return [];
    const rawSegments = journeyData?.segments;
    const segments = Array.isArray(rawSegments) ? rawSegments : [];

    return segments
      .flatMap((segment, segmentIndex) => {
        const segmentPoints = Array.isArray(segment.points) ? segment.points : [];
        const firstPoint = segmentPoints[0] ?? null;
        const matchingTimelineItem = Array.isArray(timelineItems)
          ? timelineItems.find(
              (item) => item.startTime === formatTimeHM(segment.start_time) && item.type === segment.type
            )
          : undefined;

        if (segmentPoints.length === 0) {
          const rowDateKey = segment.start_time.slice(0, 10);
          const coords = resolvePointCoordinates(firstPoint);
          const pointIndex = resolvePointIndexInAllPoints(safePoints, segmentIndex, firstPoint, firstPoint?.id ?? null);
          return [
            {
              id: `${segment.start_time}-${segment.type}-${segmentIndex}`,
              segmentIndex,
              segmentIndices: [segmentIndex],
              segmentType: segment.type,
              pointId: firstPoint?.id ?? null,
              pointIndex,
              latitude: coords?.latitude ?? null,
              longitude: coords?.longitude ?? null,
              dateKey: rowDateKey,
              sortKey: segment.start_time,
              time: formatTimeHM(segment.start_time),
              speed: segment.type === 'route' ? t('empty.placeholder') : formatDuration(segment.status_duration),
              statusDuration: segment.status_duration,
              durationSeconds: durationStrToSeconds(segment.status_duration),
              address:
                (typeof segment.start_address === 'string' && segment.start_address.trim()) ||
                matchingTimelineItem?.displayValue ||
                t('empty.placeholder'),
              battery:
                firstPoint?.battery_percent != null
                  ? formatBattery(firstPoint.battery_percent)
                  : t('empty.placeholder'),
            },
          ];
        }

        const pointsToRender = segment.type === 'route' ? segmentPoints : segmentPoints.slice(0, 1);

        return pointsToRender.map((point, localPointIndex) => {
          const rowDateKey = point.timestamp.slice(0, 10);
          const speedValue =
            segment.type === 'route'
              ? point.speed != null && Number.isFinite(point.speed)
                ? `${point.speed} km/h`
                : t('empty.placeholder')
              : formatDuration(segment.status_duration);

          const addressValue =
            typeof point.address === 'string' && point.address.trim()
              ? point.address
              : typeof segment.start_address === 'string' && segment.start_address.trim()
                ? segment.start_address
                : matchingTimelineItem?.displayValue || t('empty.placeholder');

          const batterySource = segment.type === 'route' ? point.battery_percent : firstPoint?.battery_percent;
          const coords = resolvePointCoordinates(point);
          const pointIndex = resolvePointIndexInAllPoints(safePoints, segmentIndex, point, point.id ?? null);

          return {
            id: `${segment.start_time}-${segment.type}-${segmentIndex}-${point.id || localPointIndex}`,
            segmentIndex,
            segmentIndices: [segmentIndex],
            segmentType: segment.type,
            pointId: point.id ?? null,
            pointIndex,
            latitude: coords?.latitude ?? null,
            longitude: coords?.longitude ?? null,
            dateKey: rowDateKey,
            sortKey: point.timestamp || segment.start_time,
            time: formatTimeHM(point.timestamp || segment.start_time),
            speed: speedValue,
            statusDuration: segment.status_duration,
            durationSeconds: durationStrToSeconds(segment.status_duration),
            address: addressValue,
            battery: batterySource != null ? formatBattery(batterySource) : t('empty.placeholder'),
          };
        });
      })
      .filter((row) => {
        if (isAllDatesSelected) return true;
        if (!activeDateKey) return false;
        return row.dateKey === activeDateKey;
      })
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [activeDateKey, hasValidDateRange, isAllDatesSelected, journeyData?.segments, safePoints, t, timelineItems]);

  const mergedDetailRows = useMemo<DetailRow[]>(() => {
    const merged: DetailRow[] = [];

    for (const row of detailRows) {
      const prev = merged[merged.length - 1];
      const isStationary = row.segmentType !== 'route';
      const canMergeWithPrev =
        prev !== undefined && isStationary && prev.segmentType === row.segmentType;

      if (canMergeWithPrev) {
        prev.durationSeconds += row.durationSeconds;
        prev.statusDuration = secondsToDurationStr(prev.durationSeconds);
        prev.segmentIndices = [...prev.segmentIndices, ...row.segmentIndices];
        continue;
      }

      merged.push({ ...row, segmentIndices: [...row.segmentIndices] });
    }

    return merged;
  }, [detailRows]);

  const selectDateByKey = (dateKey: string) => {
    setHoveredDropdownOptionKey(null);

    if (dateKey === 'all') {
      setIsAllDatesSelected(true);
      if (onSelectedDateChange) onSelectedDateChange(null);
      setIsDateDropdownOpen(false);
      return;
    }

    const found = rangeDates.find((d) => d.key === dateKey);
    if (found && onSelectedDateChange) {
      setIsAllDatesSelected(false);
      onSelectedDateChange(found.date);
    }
    setIsDateDropdownOpen(false);
  };

  useEffect(() => {
    if (!selectedDate) {
      setIsAllDatesSelected(true);
      return;
    }

    const selectedKey = format(selectedDate, 'yyyy-MM-dd');
    const existsInRange = rangeDates.some((d) => d.key === selectedKey);
    if (existsInRange) {
      setIsAllDatesSelected(false);
    } else {
      setIsAllDatesSelected(true);
    }
  }, [rangeDates, selectedDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsDateDropdownOpen(false);
        setHoveredDropdownOptionKey(null);
      }
    };

    if (isDateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDateDropdownOpen]);

  const isSingleDayRange = Boolean(dateRange.start && dateRange.end && isSameDay(dateRange.start, dateRange.end));
  const dropdownButtonLabel = isAllDatesSelected
    ? t('sidebar.allDates')
    : rangeDates.find((d) => d.key === activeDateKey)?.label || t('sidebar.allDates');
  const hasLongOptionsList = rangeDates.length + 1 > 5;
  const shouldMuteActiveDropdownRow = hoveredDropdownOptionKey !== null;
  const formatDurationCompact = (durationStr: string): string => {
    const totalSeconds = durationStrToSeconds(durationStr);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    if (totalSeconds <= 0) return t('format.short.zeroDuration');

    const parts: string[] = [];
    if (h > 0) parts.push(`${h}${t('format.short.hour')}`);
    if (m > 0) parts.push(`${m}${t('format.short.minute')}`);
    if (s > 0) parts.push(`${s}${t('format.short.second')}`);
    return parts.join('');
  };
  const activePoint =
    enableActiveHighlight && safePoints.length > 0
      ? safePoints[Math.min(currentPointIndex, safePoints.length - 1)]
      : null;

  const getNonRouteSpeedLabel = useCallback(
    (segmentType: DetailRow['segmentType']) => {
      switch (segmentType) {
        case 'stop':
          return t('sidebar.segmentStatus.stop');
        case 'lost_gprs':
          return t('sidebar.segmentStatus.lost_gprs');
        case 'lost_gps':
          return t('sidebar.segmentStatus.lost_gps');
        default:
          return t('sidebar.standingStill');
      }
    },
    [t]
  );

  const handleDetailRowClick = useCallback(
    (row: DetailRow) => {
      if (!onDetailRowClick) return;
      onDetailRowClick(toSidebarDetailRowClickPayload(row));
    },
    [onDetailRowClick]
  );

  return (
    <Box className='flex min-h-[797px] flex-1 flex-col gap-4 overflow-hidden rounded-xl border border-gray-200 bg-white px-3 pb-4 pt-3 shadow-sm'>
      <Box className='flex items-start justify-between gap-2'>
        <Typography
          component='h3'
          className='m-0 mt-2 text-[18px] font-semibold leading-[140%] text-[#161A1F]'
          style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0' }}
        >
          {t('sidebar.detailTitle')}
        </Typography>
        {!isSingleDayRange && (
          <Box ref={dropdownRef} className='relative'>
            <Button
              variant='text'
              className='flex h-12 w-[200px] normal-case items-center justify-between gap-2 rounded-xl bg-white px-3.5 py-3.5'
              style={{ border: '2px solid var(--Border-color_border_default, #D7DEE7)' }}
              onClick={() => {
                setIsDateDropdownOpen((prev) => {
                  const nextOpen = !prev;
                  if (!nextOpen) {
                    setHoveredDropdownOptionKey(null);
                  }
                  return nextOpen;
                });
              }}
            >
              <Typography
                component='span'
                className='text-[16px] font-normal leading-[140%] text-[#404854]'
                style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
              >
                {dropdownButtonLabel}
              </Typography>
              <MapDownIcon className='h-4 w-4 text-[#404854]' />
            </Button>

            {isDateDropdownOpen && (
              <Box className='absolute right-0 top-[calc(100%+8px)] z-30 h-48 w-[200px] rounded-xl bg-white p-2 shadow-[0px_0px_6px_0px_#509EE833]'>
                <Box
                  className={hasLongOptionsList ? 'max-h-[176px] overflow-y-auto pr-1' : ''}
                  onMouseLeave={() => setHoveredDropdownOptionKey(null)}
                >
                  <Button
                    variant='text'
                    className={`mb-1 flex h-10 w-full normal-case items-center rounded-lg px-2 text-left text-[16px] font-normal leading-[140%] text-[#404854] ${
                      isAllDatesSelected && !shouldMuteActiveDropdownRow ? 'bg-[#F3F6F9]' : 'hover:bg-[#F3F6F9]'
                    }`}
                    style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
                    onMouseEnter={() => setHoveredDropdownOptionKey('all')}
                    onClick={() => selectDateByKey('all')}
                  >
                    {t('sidebar.allDates')}
                  </Button>
                  {rangeDates.map((d) => {
                    const isActive = !isAllDatesSelected && activeDateKey === d.key;
                    return (
                      <Button
                        key={d.key}
                        variant='text'
                        className={`mb-1 flex h-10 w-full normal-case items-center rounded-lg px-2 text-left text-[16px] font-normal leading-[140%] text-[#404854] ${
                          isActive && !shouldMuteActiveDropdownRow ? 'bg-[#F3F6F9]' : 'hover:bg-[#F3F6F9]'
                        }`}
                        style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
                        onMouseEnter={() => setHoveredDropdownOptionKey(d.key)}
                        onClick={() => selectDateByKey(d.key)}
                      >
                        {d.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box className='flex flex-col gap-2'>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} className='h-6 w-full animate-pulse rounded bg-gray-200' />
          ))}
        </Box>
      ) : !hasValidDateRange ? (
        <Typography className='text-center text-sm text-gray-400'>{t('empty.selectDateRangePrompt')}</Typography>
      ) : mergedDetailRows.length === 0 && (!safePoints || safePoints.length === 0) ? (
        <Typography className='text-center text-sm text-gray-400'>{t('empty.noData')}</Typography>
      ) : mergedDetailRows.length === 0 ? (
        <Typography className='text-center text-sm text-gray-400'>{t('empty.noDataForDate')}</Typography>
      ) : (
        <Box className='flex h-[696px] flex-col overflow-hidden rounded-xl border border-[#D7DEE7] bg-white'>
          <Box className='shrink-0 border-b border-[#D7DEE7] bg-[#E3E7EC] pr-1'>
            <table className='w-full border-separate border-spacing-0 text-xs' style={{ tableLayout: 'fixed' }}>
              <DetailTableColGroup />
              <thead className='bg-[#E3E7EC]'>
                <tr>
                  <th
                    className='border-r border-[#D7DEE7] bg-[#E3E7EC] px-3 py-3 text-left text-[16px] font-normal leading-[140%] text-[#404854]'
                    style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
                  >
                    {t('sidebar.table.time')}
                  </th>
                  <th
                    className='border-r border-[#D7DEE7] bg-[#E3E7EC] px-3 py-3 text-left text-[16px] font-normal leading-[140%] text-[#404854]'
                    style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
                  >
                    {t('sidebar.table.speed')}
                  </th>
                  <th
                    className='border-r border-[#D7DEE7] bg-[#E3E7EC] px-3 py-3 text-left text-[16px] font-normal leading-[140%] text-[#404854]'
                    style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
                  >
                    {t('sidebar.table.location')}
                  </th>
                  <th
                    className='bg-[#E3E7EC] px-3 py-3 text-left text-[16px] font-normal leading-[140%] text-[#404854]'
                    style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
                  >
                    {t('sidebar.table.battery')}
                  </th>
                </tr>
              </thead>
            </table>
          </Box>

          <Box className='flex-1 overflow-hidden'>
            <TableVirtuoso<DetailRow, { onRowClick?: (row: DetailRow) => void; onRowHover?: (rowId: string | null) => void }>
              style={{ height: '100%' }}
              data={mergedDetailRows}
              context={{ onRowClick: handleDetailRowClick, onRowHover: setHoveredDetailRowId }}
              fixedItemHeight={56}
              overscan={14}
              components={{
                Scroller: DetailTableScroller,
                Table: DetailTableElement,
                TableRow: ClickableDetailTableRow as typeof DetailTableRowElement,
              }}
              itemContent={(index: number, row: DetailRow): React.ReactNode => {
                const isActive =
                  activePoint !== null &&
                  (row.segmentType === 'route'
                    ? row.segmentIndex === activePoint.segmentIndex &&
                      (row.pointId == null || row.pointId === activePoint.id)
                    : row.segmentIndices.includes(activePoint.segmentIndex));
                const rowColor = getJourneySegmentTypeColor(row.segmentType, personStatuses);
                const activeBg = getJourneySegmentTypeActiveBg(row.segmentType, personStatuses);
                const isHovered = hoveredDetailRowId === row.id;
                const rowBackground = isActive || isHovered ? activeBg : undefined;

                return (
                  <>
                    <td
                      className='px-3 py-2 align-middle text-[16px] font-normal leading-[140%]'
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '16px',
                        letterSpacing: '0.02em',
                        color: rowColor,
                        backgroundColor: rowBackground,
                        boxShadow: index === mergedDetailRows.length - 1 ? 'none' : 'inset 0 -1px 0 #D7DEE7',
                      }}
                    >
                      {row.time}
                    </td>
                    <td
                      className='px-3 py-2 align-middle text-[16px] font-normal leading-[140%]'
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '16px',
                        letterSpacing: '0.02em',
                        color: rowColor,
                        backgroundColor: rowBackground,
                        boxShadow: index === mergedDetailRows.length - 1 ? 'none' : 'inset 0 -1px 0 #D7DEE7',
                      }}
                    >
                      {row.segmentType === 'route' ? (
                        row.speed
                      ) : (
                        <Box component='span' className='flex flex-col text-[16px] leading-[140%]'>
                          <Typography component='span' variant='inherit' style={{ fontSize: 'inherit' }}>
                            {getNonRouteSpeedLabel(row.segmentType)}
                          </Typography>
                          <Typography component='span' variant='inherit' style={{ fontSize: 'inherit' }}>
                            {formatDurationCompact(row.statusDuration)}
                          </Typography>
                        </Box>
                      )}
                    </td>
                    <td
                      className='px-3 py-2 align-middle text-[16px] font-normal leading-[140%]'
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '16px',
                        letterSpacing: '0.02em',
                        color: rowColor,
                        backgroundColor: rowBackground,
                        boxShadow: index === mergedDetailRows.length - 1 ? 'none' : 'inset 0 -1px 0 #D7DEE7',
                      }}
                    >
                      <Tooltip
                        title={row.address}
                        arrow
                        placement='top-start'
                        enterDelay={250}
                        slotProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#FFFFFF',
                              color: rowColor,
                              border: `1px solid ${rowColor}`,
                              boxShadow: '0 8px 24px rgba(22, 26, 31, 0.12)',
                              borderRadius: '10px',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '140%',
                              letterSpacing: '0.01em',
                              maxWidth: 420,
                              px: 1.5,
                              py: 1,
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#FFFFFF',
                              '&:before': {
                                border: `1px solid ${rowColor}`,
                              },
                            },
                          },
                        }}
                      >
                        <Typography
                          component='span'
                          variant='inherit'
                          className='block cursor-help overflow-hidden text-ellipsis whitespace-nowrap'
                          style={{ fontSize: 'inherit' }}
                        >
                          {row.address}
                        </Typography>
                      </Tooltip>
                    </td>
                    <td
                      className='px-3 py-2 align-middle text-[16px] font-normal leading-[140%]'
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontSize: '16px',
                        letterSpacing: '0.02em',
                        color: rowColor,
                        backgroundColor: rowBackground,
                        boxShadow: index === mergedDetailRows.length - 1 ? 'none' : 'inset 0 -1px 0 #D7DEE7',
                      }}
                    >
                      {row.battery}
                    </td>
                  </>
                );
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function UserInfoCard({ journeyData, selectedPersonInfo, isLoading }: JourneySidebarProps) {
  const { t } = useTranslation('admin/journey');
  const displayName = selectedPersonInfo?.full_name?.trim() || '';
  const displayImei = selectedPersonInfo?.device_imei || journeyData?.device_imei || '';

  return (
    <Box className='flex min-h-[79px] flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm'>
      <Typography
        component='h3'
        className='m-0 text-[18px] font-semibold leading-[140%] text-[#161A1F]'
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        {displayName || t('sidebar.unknownUser')}
      </Typography>

      {isLoading ? (
        <Box className='flex flex-col gap-2'>
          <Box className='h-4 w-3/4 animate-pulse rounded bg-gray-200' />
          <Box className='h-3 w-1/2 animate-pulse rounded bg-gray-200' />
        </Box>
      ) : (
        <Typography
          component='div'
          className='text-[16px] font-normal leading-[140%] text-[#161A1F]'
          style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em' }}
        >
          {t('sidebar.imei', { value: displayImei })}
        </Typography>
      )}
    </Box>
  );
}

const JourneySidebar: React.FC<JourneySidebarProps> = ({
  journeyData,
  selectedPersonInfo,
  dateRange,
  timelineItems,
  allPoints,
  selectedDate,
  onSelectedDateChange,
  isLoading,
  hasValidDateRange = false,
  currentPointIndex,
  enableActiveHighlight,
  onDetailRowClick,
  activeTab = 'timeline',
  onTabChange,
  onTimelineSegmentClick,
  selectedTimelineSegmentIndex,
  onTimelineSegmentDoubleClick,
}) => {
  const { t } = useTranslation('admin/journey');

  return (
    <Box className='flex min-h-[1153px] flex-col gap-5'>
      <UserInfoCard
        journeyData={journeyData}
        selectedPersonInfo={selectedPersonInfo}
        dateRange={dateRange}
        isLoading={isLoading}
      />
      <SummaryStatsCard journeyData={journeyData} dateRange={dateRange} isLoading={isLoading} />

      <Box className='flex flex-col gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white p-3 shadow-sm'>
        <Box className='flex items-center justify-between gap-2'>
          <Typography
            component='h3'
            className='m-0 text-[18px] font-semibold leading-[140%] text-[#161A1F]'
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('map.title')}
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(_, nextTab) => onTabChange?.(nextTab as 'timeline' | 'detail')}
            variant='fullWidth'
            sx={{
              minHeight: 36,
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTab-root': {
                minHeight: 32,
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 600,
                color: '#4B5563',
                borderRadius: '8px',
                minWidth: 120,
                padding: '4px 10px',
              },
              '& .Mui-selected': { color: '#0F172A', backgroundColor: '#F3F6F9' },
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <Tab label='Lộ trình' value='timeline' />
            <Tab label='Chi tiết' value='detail' />
          </Tabs>
        </Box>

        {activeTab === 'timeline' ? (
          <Box className='h-[540px]'>
            <JourneyTimeline
              timelineItems={timelineItems ?? []}
              dateRange={dateRange}
              selectedDate={selectedDate}
              selectedSegmentIndex={selectedTimelineSegmentIndex ?? null}
              enableActiveHighlight={Boolean(enableActiveHighlight)}
              hasValidDateRange={hasValidDateRange}
              onSegmentClick={onTimelineSegmentClick}
              onSegmentDoubleClick={onTimelineSegmentDoubleClick}
              isLoading={isLoading}
            />
          </Box>
        ) : (
          <DetailTableCard
            journeyData={journeyData}
            dateRange={dateRange}
            timelineItems={timelineItems}
            allPoints={allPoints}
            selectedDate={selectedDate}
            onSelectedDateChange={onSelectedDateChange}
            isLoading={isLoading}
            hasValidDateRange={hasValidDateRange}
            currentPointIndex={currentPointIndex}
            enableActiveHighlight={enableActiveHighlight}
            onDetailRowClick={onDetailRowClick}
          />
        )}
      </Box>
    </Box>
  );
};

export default React.memo(JourneySidebar);
